import React, { useState } from "react";
import "./EntrepreneurWorkSpace.css";
import PromptBar from "./PromptBar";
import RapidMindAgentButton from "./RapidMindAgentButton";
import ChatHistoryItem from "../modules/Historical List/ChatHistoryItem";

const EntrepreneurWorkSpace = () => {
  const [transactions, setTransactions] = useState([
    { id: 1, description: "Product Sales", amount: 1500 },
    { id: 2, description: "Ad Spend", amount: -500 },
    { id: 3, description: "Hosting Fees", amount: -100 },
  ]);

  const [newTransaction, setNewTransaction] = useState({
    description: "",
    amount: "",
  });

  const handleAddTransaction = () => {
    if (!newTransaction.description || !newTransaction.amount) return;
    setTransactions([
      ...transactions,
      {
        id: transactions.length + 1,
        description: newTransaction.description,
        amount: parseFloat(newTransaction.amount),
      },
    ]);
    setNewTransaction({ description: "", amount: "" });
  };

  const netProfit = transactions.reduce((acc, curr) => acc + curr.amount, 0);

  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [aiResponse, setAiResponse] = useState('');
  const [chatMessages, setChatMessages] = useState([]);

  const handleSearch = async (query, results = null) => {
    if (!query.trim()) return;

    setIsSearching(true);
    try {
      // If results are provided from PromptBar, use them
      if (results && results.results && results.results.length > 0) {
        console.log('Using web search results from PromptBar:', results);
        setSearchResults(results.results); // Set the actual results array
        setIsSearching(false);
        return;
      }

      // Fallback: perform search directly if no results provided
      const response = await fetch('/api/search/files', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          query: query.trim(),
          maxResults: 10,
          caseSensitive: false
        }),
        credentials: 'include',
      });

      if (response.ok) {
        const searchResults = await response.json();
        setSearchResults(searchResults.results || []);
      } else {
        console.error('Search failed');
        setSearchResults([]);
      }
    } catch (error) {
      console.error('Search error:', error);
      setSearchResults([]);
    } finally {
      setIsSearching(false);
    }
  };

  return (
    <div id='EntrepreneurWorkSpace'  className="entrepreneur-workspace">
      <h1 className="workspace-title">Entrepreneur WorkSpace</h1>

      {/* Prompt Bar */}
      <div style={{ padding: '16px', background: '#f8f9fa' }}>
        <PromptBar
          onSearch={handleSearch}
          onAIResponse={(response) => {
            setAiResponse(response);
            setChatMessages(prev => [...prev, { type: 'ai', content: response, timestamp: new Date() }]);
          }}
          onSearchResults={(results) => {
            setSearchResults(results);
            setChatMessages(prev => [...prev, { type: 'search', content: results, timestamp: new Date() }]);
          }}
        />
      </div>

      {/* Chat Area */}
      <div className="chat-container">
        {chatMessages.map((message, idx) => (
          <div key={idx} className={`chat-message ${message.type}`}>
            {message.type === 'ai' && (
              <div className="ai-message">
                <div className="ai-avatar">🤖</div>
                <div className="ai-content">
                  <pre>{message.content}</pre>
                </div>
              </div>
            )}
            {message.type === 'search' && (
              <div className="search-message">
                <div className="search-avatar">🔍</div>
                <div className="search-content">
                  <h4>Search Results:</h4>
                  {message.content.map((result, resultIdx) => (
                    <div key={resultIdx} className="search-result-item">
                      <h5>
                        <a href={result.url} target="_blank" rel="noopener noreferrer">
                          {result.title}
                        </a>
                      </h5>
                      <p>{result.description}</p>
                      <small>{result.url}</small>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        ))}
      </div>

      {/* PromptBar Layout with Historical Research and Summarize */}
      <div className="promptbar-layout">
        {/* Historical Research Section */}
        <div className="historical-research-section">
          <h4>📚 Historical Research</h4>
          <div className="historical-list">
            <ChatHistoryItem
              title="Business Plan Analysis"
              date="1 hour ago"
              onClick={() => console.log('Load business plan history')}
            />
            <ChatHistoryItem
              title="Market Research Data"
              date="5 hours ago"
              onClick={() => console.log('Load market research history')}
            />
            <ChatHistoryItem
              title="Financial Projections"
              date="2 days ago"
              onClick={() => console.log('Load financial history')}
            />
            <ChatHistoryItem
              title="Competitor Analysis"
              date="1 week ago"
              onClick={() => console.log('Load competitor history')}
            />
          </div>
        </div>

        {/* Quick Summarize Section */}
        <div className="quick-summarize-section">
          <h4>⚡ Quick Summarize</h4>
          <div className="summarize-input-container">
            <textarea
              className="summarize-textarea"
              placeholder="Paste business text here to summarize..."
              rows="6"
            />
            <button className="summarize-button">Summarize</button>
          </div>
          <div className="summarize-response">
            <pre>Summary will appear here...</pre>
          </div>
        </div>
      </div>

      {/* KPIs */}
      <div className="kpi-container">
        <div className="kpi-card">
          <h3>Total Revenue</h3>
          <p>${transactions.filter(t => t.amount > 0).reduce((a, b) => a + b.amount, 0)}</p>
        </div>
        <div className="kpi-card">
          <h3>Total Expenses</h3>
          <p>${Math.abs(transactions.filter(t => t.amount < 0).reduce((a, b) => a + b.amount, 0))}</p>
        </div>
        <div className="kpi-card net-profit">
          <h3>Net Profit</h3>
          <p>${netProfit}</p>
        </div>
      </div>

      {/* Transactions Table */}
      <div className="transactions-section">
        <h2>Transactions</h2>
        <table className="transactions-table">
          <thead>
            <tr>
              <th>Description</th>
              <th>Amount ($)</th>
            </tr>
          </thead>
          <tbody>
            {transactions.map((t) => (
              <tr key={t.id}>
                <td>{t.description}</td>
                <td className={t.amount < 0 ? "negative" : "positive"}>{t.amount}</td>
              </tr>
            ))}
          </tbody>
        </table>

        {/* Add Transaction */}
        <div className="add-transaction">
          <input
            type="text"
            placeholder="Description"
            value={newTransaction.description}
            onChange={(e) =>
              setNewTransaction({ ...newTransaction, description: e.target.value })
            }
          />
          <input
            type="number"
            placeholder="Amount"
            value={newTransaction.amount}
            onChange={(e) =>
              setNewTransaction({ ...newTransaction, amount: e.target.value })
            }
          />
          <button onClick={handleAddTransaction}>Add</button>
        </div>
      </div>

      {/* Search Results Display */}
      {searchResults.length > 0 && (
        <div className="search-results-section">
          <h2>🔍 Search Results</h2>
          <div className="search-results">
            {searchResults.map((result, idx) => (
              <div key={idx} className="search-result-item">
                <h4>
                  <a href={result.url} target="_blank" rel="noopener noreferrer">
                    {result.title}
                  </a>
                </h4>
                <p>{result.description}</p>
                <small>{result.url}</small>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Risk Management Section */}
      <div className="risk-section">
        <h2>Risk Management</h2>
        <p>Simulate your expenses and revenue to calculate risks.</p>
        <button className="risk-btn">Run Risk Analysis</button>
      </div>
    </div>
  );
};

export default EntrepreneurWorkSpace;
